import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { HashLink } from './heading.js';
import { MyST } from './MyST.js';
function getClasses(className) {
    var _a;
    const classes = (_a = className === null || className === void 0 ? void 0 : className.split(' ').map((s) => s.trim().toLowerCase()).filter((s) => !!s)) !== null && _a !== void 0 ? _a : [];
    return [...new Set(classes)];
}
function getColor({ classes = [] }, defaultColor = 'blue') {
    if (classes.includes('gray'))
        return { color: 'gray' };
    if (classes.includes('purple'))
        return { color: 'purple' };
    if (classes.includes('yellow'))
        return { color: 'yellow' };
    if (classes.includes('orange'))
        return { color: 'orange' };
    if (classes.includes('green'))
        return { color: 'green' };
    if (classes.includes('red'))
        return { color: 'red' };
    if (classes.includes('blue'))
        return { color: 'blue' };
    return { color: defaultColor };
}
const WrapperElement = ({ id, dropdown, className, children, }) => {
    if (dropdown)
        return (_jsx("details", { id: id, className: className, children: children }));
    return (_jsx("aside", { id: id, className: className, children: children }));
};
const HeaderElement = ({ dropdown, className, children, }) => {
    if (dropdown)
        return _jsx("summary", { className: className, children: children });
    return _jsx("div", { className: className, children: children });
};
const iconClass = 'inline-block pl-2 mr-2 self-center flex-none';
export function Callout({ title, color, dropdown, children, identifier, Icon, }) {
    return (_jsxs(WrapperElement, { id: identifier, dropdown: dropdown, className: classNames('my-5 shadow dark:bg-stone-800 overflow-hidden', 'dark:border-l-4 border-slate-400', {
            'dark:border-gray-500/60': !color || color === 'gray',
            'dark:border-blue-500/60': color === 'blue',
            'dark:border-green-500/60': color === 'green',
            'dark:border-amber-500/70': color === 'yellow',
            'dark:border-orange-500/60': color === 'orange',
            'dark:border-red-500/60': color === 'red',
            'dark:border-purple-500/60': color === 'purple',
        }), children: [_jsxs(HeaderElement, { dropdown: dropdown, className: classNames('m-0 font-medium py-2 flex min-w-0', 'text-md', 'border-y dark:border-y-0', {
                    'bg-gray-50/80 dark:bg-slate-900': !color || color === 'gray',
                    'bg-blue-50/80 dark:bg-slate-900': color === 'blue',
                    'bg-green-50/80 dark:bg-slate-900': color === 'green',
                    'bg-amber-50/80 dark:bg-slate-900': color === 'yellow',
                    'bg-orange-50/80 dark:bg-slate-900': color === 'orange',
                    'bg-red-50/80 dark:bg-slate-900': color === 'red',
                    'bg-purple-50/80 dark:bg-slate-900': color === 'purple',
                    'cursor-pointer hover:shadow-[inset_0_0_0px_30px_#00000003] dark:hover:shadow-[inset_0_0_0px_30px_#FFFFFF03]': dropdown,
                }), children: [Icon && (_jsx(Icon, { width: "2rem", height: "2rem", className: classNames('inline-block pl-2 mr-2 self-center flex-none', classNames({
                            'text-gray-600': !color || color === 'gray',
                            'text-blue-600': color === 'blue',
                            'text-green-600': color === 'green',
                            'text-amber-600': color === 'yellow',
                            'text-orange-600': color === 'orange',
                            'text-red-600': color === 'red',
                            'text-purple-600': color === 'purple',
                        })) })), _jsx("div", { className: classNames('text-neutral-900 dark:text-white grow self-center overflow-hidden break-words', { 'ml-4': !Icon }, // No icon!
                        'group'), children: title }), dropdown && (_jsx("div", { className: "self-center flex-none text-sm font-thin text-neutral-700 dark:text-neutral-200", children: _jsx(ChevronRightIcon, { width: "1.5rem", height: "1.5rem", className: classNames(iconClass, 'transition-transform details-toggle') }) }))] }), _jsx("div", { className: classNames('px-4', { 'details-body': dropdown }), children: children })] }));
}
export const ExerciseRenderer = ({ node }) => {
    var _a, _b, _c;
    if (node.hidden)
        return null;
    const [title, ...rest] = (_a = node.children) !== null && _a !== void 0 ? _a : [];
    const classes = getClasses(node.class);
    const { color } = getColor({ classes });
    const isDropdown = classes.includes('dropdown');
    const useTitle = ((_c = (_b = node.children) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.type) === 'admonitionTitle';
    const identifier = node.html_id;
    const enumerator = node.enumerator;
    const titleNode = (_jsxs(_Fragment, { children: [_jsxs(HashLink, { id: identifier, kind: "Exercise", children: [node.gate === 'start' && 'Start of ', node.gate === 'end' && 'End of ', "Exercise", enumerator != null && _jsxs(_Fragment, { children: [" ", enumerator] })] }), useTitle && (_jsxs(_Fragment, { children: [' ', "(", _jsx(MyST, { ast: [title] }), ")"] }))] }));
    return (_jsx(Callout, { identifier: identifier, title: titleNode, color: color, dropdown: isDropdown, children: useTitle ? _jsx(MyST, { ast: rest }) : _jsx(MyST, { ast: node.children }) }));
};
export const SolutionRenderer = ({ node }) => {
    var _a, _b, _c;
    if (node.hidden)
        return null;
    const [title, ...rest] = (_a = node.children) !== null && _a !== void 0 ? _a : [];
    const classes = getClasses(node.class);
    const { color } = getColor({ classes }, 'gray');
    const isDropdown = classes.includes('dropdown');
    const useTitle = ((_c = (_b = node.children) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.type) === 'admonitionTitle';
    const identifier = node.html_id;
    const titleNode = (_jsxs(_Fragment, { children: [node.gate === 'start' && 'Start of ', node.gate === 'end' && 'End of ', _jsx(MyST, { ast: [title] }), _jsx(HashLink, { id: identifier, kind: "Solution", hover: true, hideInPopup: true, children: ' #' })] }));
    return (_jsx(Callout, { identifier: identifier, title: useTitle ? titleNode : undefined, color: color, dropdown: isDropdown, children: useTitle ? _jsx(MyST, { ast: rest }) : _jsx(MyST, { ast: node.children }) }));
};
const EXERCISE_RENDERERS = {
    exercise: ExerciseRenderer,
    solution: SolutionRenderer,
};
export default EXERCISE_RENDERERS;
